import React, { useState } from 'react';
import styled from 'styled-components';
import { useProjectElementListStore } from '../../shared/store/projectElementStore';
import { useEditMode } from '../../shared/hooks/useEditMode';
import { useValidation } from '../../shared/hooks/useValidation';
import { useStandardAlertStore } from '../../shared/store/portal/alertStore';
import { getAlertMessage } from '../../shared/constants/alertMessages';
import { openNewTab } from '../../shared/util/openNewTab';
import { AlertPosition, AlertType, DisplayAlignment, DisplaySize, ImageStyle } from '../../shared/enum/EnumRepository';
import { WorkDetailData } from '../../shared/dto/EntityRepository';
import { StyledImgProps } from '../../shared/dto/StyleCompRepository';
import { convertS3UrlToCloudFrontUrl } from '../../shared/aws/awsHelper';
import OptimizedMoleculeImg from '../../shared/component/molecule/OptimizedMoleculeImg';
import { parseSupportedVideoUrl } from '../../shared/util/videoUrl';
import { ImgInPeWrapper, WorkImageForLandscape, WorkImageForPortrait } from './WorkImgInPe';
import UniversalIconBtn from '../../shared/component/headless/button/UniversalIconBtn';
import playIcon from '../../asset/icon/play.png';
export interface DetailVideoInPeProps {
  peId: string;
  alignment: DisplayAlignment;
  displaySize: DisplaySize;
  data: WorkDetailData;
  priority: boolean;
  imageStyle: ImageStyle;
}

const DetailVideoInPe: React.FC<DetailVideoInPeProps> = ({
  peId,
  alignment,
  displaySize,
  data,
  priority,
  imageStyle,
}) => {
  const handleImageChange = useProjectElementListStore((s) => s.updateImage);
  const { isEditMode } = useEditMode();
  const { isValidVideoUrl } = useValidation();
  const { enqueueAlert } = useStandardAlertStore();
  const [isHovered, setIsHovered] = useState(false);

  const convertedCloudFrontUrl = convertS3UrlToCloudFrontUrl(data.uploadFile.originUrl);
  const imageRatio = data.uploadFile?.width && data.uploadFile?.height
    ? data.uploadFile.width / data.uploadFile.height
    : undefined;

  const moveToVideoUrl = () => {
    if (!data.videoUrl) {
      enqueueAlert({
        type: AlertType.ALERT,
        position: AlertPosition.TOP,
        content: getAlertMessage('VIDEO_URL_NULL'),
      });
      return;
    }
    if (!isValidVideoUrl(data.videoUrl)) return;
    const videoUrl = parseSupportedVideoUrl(data.videoUrl);
    if (!videoUrl) return;
    enqueueAlert({
      type: AlertType.CONFIRM,
      position: AlertPosition.TOP,
      content: getAlertMessage('CONFIRM_MOVE_TO_VIDEO_URL_PLATFORM', { platform: videoUrl.platform }),
      callBack: () => openNewTab(videoUrl.url.href),
    });
  };

  const getStyledImage = () => {
    if (imageStyle === ImageStyle.LANDSCAPE) {
      return WorkImageForLandscape as React.ComponentType<StyledImgProps>;
    } else {
      return WorkImageForPortrait as React.ComponentType<StyledImgProps>;
    }
  };

  return (
    <ImgInPeWrapper
      $alignment={alignment || DisplayAlignment.CENTER}
      $displaySize={displaySize || DisplaySize.BIG}
      $imageStyle={imageStyle || ImageStyle.LANDSCAPE}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <ImageWrap>
        <OptimizedMoleculeImg
          srcUrl={convertedCloudFrontUrl}
          alt={data.description || 'Detail Cut'}
          displaySize={displaySize}
          displayAlignment={alignment || DisplayAlignment.CENTER}
          handleChange={(originUrl: string, width: number, height: number) =>
            handleImageChange(peId, originUrl, width, height)
          }
          StyledImg={getStyledImage()}
          priority={priority}
          imageRatio={imageRatio}
        />
        {!isEditMode && (
          <PlayOverlay $isHovered={isHovered}>
            <UniversalIconBtn
              size="standard"
              border={false}
              backgroundImg={playIcon}
              onClick={moveToVideoUrl}
            />
          </PlayOverlay>
        )}
      </ImageWrap>
    </ImgInPeWrapper>
  );
};

const ImageWrap = styled.div`
  position: relative;
  width: fit-content;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const PlayOverlay = styled.div<{ $isHovered: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
  opacity: ${({ $isHovered }) => ($isHovered ? 1 : 0)};
  backdrop-filter: blur(${({ $isHovered }) => ($isHovered ? 1.5 : 0)}px);
  transition: all 0.4s ease-in-out;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export default DetailVideoInPe;
